import { ImageObject } from '../resource/ImageObject'
import { sleep } from '../utils/waitUtil'
import { gsap } from 'gsap'

const getImageLayerOrder = (imageData: any) => {
  const zIndex = Number(imageData?.['z-index'])
  if (Number.isFinite(zIndex)) return zIndex
  return 0
}

export const sortDisplayedImageEntries = (displayedImages: any): Array<[string, any]> => {
  return Object.entries(displayedImages)
    .map((entry, index) => ({
      entry,
      index,
      layerOrder: getImageLayerOrder(entry[1]),
    }))
    .sort((a, b) => {
      if (a.layerOrder === b.layerOrder) {
        return a.index - b.index
      }
      return a.layerOrder - b.layerOrder
    })
    .map((item) => item.entry)
}

/*
 drawerの目的
  UIのHTMLとcanvasを描画する。
*/
export class Drawer {
  private gameScreen: HTMLElement
  private nameView!: HTMLElement
  private messageText!: HTMLElement
  private interactiveView!: HTMLElement
  private ctx!: CanvasRenderingContext2D
  private screenHtml!: HTMLElement
  private config: any
  private fadeCanvas!: HTMLCanvasElement
  private fadeCtx!: CanvasRenderingContext2D
  isSkip: boolean = false
  readySkip: boolean = false

  constructor(gameContainer: HTMLElement) {
    this.gameScreen = gameContainer
    // ウィンドウのリサイズ時にスケールを調整
    window.addEventListener('resize', () => this.adjustScale(this.gameScreen))
    // 初期ロード時にもスケールを調整
    this.adjustScale(this.gameScreen)
  }

  setScreen(screenHtml: HTMLElement, resolution: { width: number; height: number }) {
    this.screenHtml = screenHtml
    this.nameView = screenHtml.querySelector('#nameView') as HTMLElement
    this.messageText = screenHtml.querySelector('#messageView') as HTMLElement
    this.interactiveView = screenHtml.querySelector('#interactiveView') as HTMLElement

    // canvasをDOMに追加する
    const canvas = document.createElement('canvas')
    canvas.width = resolution.width || 1280
    canvas.height = resolution.height || 720
    // canvasのコンテキストを取得する
    this.gameScreen.appendChild(canvas)
    this.ctx = canvas.getContext('2d') as CanvasRenderingContext2D
    // 黒で塗りつぶす
    this.ctx.fillStyle = 'black'
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height)
    // 初期ロード時にもスケールを調整
    this.adjustScale(this.gameScreen)

    // フェード用キャンバスをDOMに追加する
    this.fadeCanvas = document.createElement('canvas')
    this.fadeCanvas.className = 'fadeCanvas'
    this.fadeCanvas.style.position = 'absolute'
    this.fadeCanvas.style.top = '0'
    this.fadeCanvas.style.left = '0'
    this.fadeCanvas.style.pointerEvents = 'none' // クリックイベントを通過させる
    this.gameScreen.insertBefore(this.fadeCanvas, document.getElementById('messageWindow'))
    this.fadeCtx = this.fadeCanvas.getContext('2d')!
    // フェード用キャンバスのサイズを設定
    this.fadeCanvas.width = resolution.width
    this.fadeCanvas.height = resolution.height
  }

  drawName(name: string) {
    this.nameView.innerHTML = name
  }

  async drawText(text: string, wait: number, containerElement?: HTMLElement) {
    let element: HTMLElement = this.messageText
    // テキストを表示するコンテナ要素を指定した場合は、その要素に追加する
    if (containerElement) {
      this.messageText.appendChild(containerElement)
      element = containerElement
    }

    // パフォーマンス最適化: テキストノードを使用してDOM再構築を回避
    const textNode = document.createTextNode('')
    element.appendChild(textNode)
    // ruby要素の場合、ルビテキスト(<rt>)をベーステキストの後に追加する
    if (element.tagName === 'RUBY' && element.dataset.rubyText) {
      const rt = document.createElement('rt')
      rt.textContent = element.dataset.rubyText
      element.appendChild(rt)
    }

    let displayedLength = 0
    for (const char of text) {
      //prettier-ignore
      setTimeout(() => { this.readySkip = true, wait });
      // 100ミリ秒待ってから、スキップボタンが押されたら即座に表示
      if (!this.isSkip) {
        textNode.textContent += char
        displayedLength++
        await sleep(wait)
      } else {
        if (this.readySkip) {
          textNode.textContent += text.slice(displayedLength)
          this.readySkip = false
          this.isSkip = false
          break
        }
      }
    }
  }

  async drawLineBreak() {
    // パフォーマンス最適化: createElement を使用
    const br = document.createElement('br')
    this.messageText.appendChild(br)
  } 

  clearText() {
    if (this.messageText) {
      this.messageText.innerHTML = ''
    }
  }

  static createDecoratedElement(element: any): HTMLElement {
    switch (element.type) {
      case 'color':
        const span = document.createElement('span')
        span.style.color = element.value
        return span
      case 'ruby':
        const ruby = document.createElement('ruby')
        ruby.dataset.rubyText = element.text
        return ruby
      case 'b':
        return document.createElement('strong')
      case 'i':
        return document.createElement('i')
      default:
        return document.createElement('span')
    }
  }

  async drawChoices(choices: any): Promise<{ selectId: number; onSelect: any }> {
    let isSelect = false
    let selectId = 0
    let onSelect = 0

    // 選択肢ボタンの配置を設定する
    const interactiveView = document.querySelector('#interactiveView') as HTMLElement
    const CHOICE_HEIGHT = 50 // 1つの選択肢の高さ（px） - button.style.heightと一致させる必要がある
    const TWO_COLUMN_THRESHOLD = 6 // 2列レイアウトに切り替える選択肢の数

    if (choices.position == 'auto' || choices.position === undefined) {
      interactiveView.className = 'auto'
      
      // 選択肢の数に応じてレイアウトを調整
      const choiceCount = choices.content.length
      
      if (choiceCount >= TWO_COLUMN_THRESHOLD) {
        // 6つ以上の選択肢がある場合、2列レイアウトに切り替え
        interactiveView.style.flexWrap = 'wrap'
        interactiveView.style.columnGap = '20px' // 列間の余白を減らす
        interactiveView.style.alignContent = 'center'
      } else {
        // 少数の選択肢の場合、通常通り表示
        interactiveView.style.flexWrap = 'wrap'
        interactiveView.style.columnGap = ''
        interactiveView.style.alignContent = 'center'
      }
    } else {
      interactiveView.className = 'manual'
    }
    // 選択肢を表示
    const choiceCount = choices.content.length
    const useTwoColumns = choiceCount >= TWO_COLUMN_THRESHOLD
    
    for (const choice of choices.content) {
      const defaultImage =
        choice.default !== undefined ? choice.default : './src/resource/system/systemPicture/02_button/button.png'
      const hoverImage =
        choice.hover !== undefined ? choice.hover : './src/resource/system/systemPicture/02_button/button2.png'
      const selectImage =
        choice.select !== undefined ? choice.select : './src/resource/system/systemPicture/02_button/button3.png'
      const button = document.createElement('div')
      button.className = 'choice'
      if (interactiveView.className == 'manual') {
        button.style.position = 'absolute'
        button.style.top = choice.position?.y || 0
        button.style.left = choice.position?.x || 0
      }
      button.style.color = choice.color !== undefined ? choice.color.default : 'black'
      // 2列レイアウトの場合は幅を調整
      button.style.width = useTwoColumns ? 'calc(50% - 10px)' : '100%'
      button.style.height = '50px'
      button.style.backgroundImage = `url(${defaultImage})`
      button.style.textAlign = 'center'
      button.style.backgroundRepeat = 'no-repeat'
      button.style.backgroundPosition = 'center'
      button.style.paddingTop = '20px'
      button.addEventListener('mouseenter', function () {
        // マウスが要素の上にあるときの背景色
        this.style.backgroundImage = `url(${hoverImage})`
        this.style.color = choice.color !== undefined ? choice.color.hover : 'black'
      })
      button.addEventListener('mouseleave', function () {
        // マウスが要素から離れたときの背景色
        this.style.backgroundImage = `url(${defaultImage})`
        this.style.color = choice.color !== undefined ? choice.color.default : 'black'
      })
      button.addEventListener('mousedown', function () {
        // マウスが要素を選択したときの背景色
        this.style.backgroundImage = `url(${selectImage})`
        this.style.color = choice.color !== undefined ? choice.color.select : 'black'
      })
      button.innerHTML = choice.label
      button.onclick = () => {
        this.interactiveView.querySelectorAll('.choice').forEach((element) => {
          element.parentNode?.removeChild(element)
        })
        selectId = choice.id
        onSelect = choice.content
        isSelect = true
      }
      this.interactiveView.appendChild(button)
    }

    // 選択待ち
    return new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (isSelect) {
          clearInterval(intervalId)
          resolve({ selectId, onSelect })
        }
      }, 100)
    })
  }

  async fadeIn(duration: number = 1000, img?: ImageObject, option?: object): Promise<void> {
    return this.fade(0, 1, duration, img, option)
  }

  async fadeOut(duration: number = 1000, img?: ImageObject, option?: object): Promise<void> {
    return this.fade(1, 0, duration, img, option)
  }

  private fade(start: number, end: number, duration: number, img?: ImageObject, option?: any): Promise<void> {
    return new Promise<void>((resolve) => {
      const startTime = performance.now()
      const pos: { x: number; y: number } = option?.pos || {
        x: 0,
        y: 0,
      }
      const size: { width: number; height: number } = option?.size || { width: this.fadeCanvas.width, height: this.fadeCanvas.height }
      const reverse: boolean = option?.look || false

      const animate = (currentTime: number) => {
        const elapsedTime = currentTime - startTime
        const progress = Math.abs(Math.min(elapsedTime / duration, 1))
        const currentAlpha = start + (end - start) * progress

        this.fadeCtx.clearRect(0, 0, this.fadeCanvas.width, this.fadeCanvas.height)
        this.fadeCtx.globalAlpha = currentAlpha
        if (img) {
          this.drawCanvas(
            img,
            pos,
            size,
            reverse,
            this.fadeCtx,
          )
        } else {
          this.fadeCtx.fillRect(0, 0, this.fadeCanvas.width, this.fadeCanvas.height)
        }

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.clear(this.fadeCtx)
          resolve()
        }
      }

      requestAnimationFrame(animate)
    })
  }

  show(displayedImages: any) {
    this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height)
    for (const [, imageData] of sortDisplayedImageEntries(displayedImages)) {
      const img: ImageObject = imageData.image
      const pos: { x: number; y: number } = imageData.pos || {
        x: 0,
        y: 0,
      }
      const size: { width: number; height: number } = imageData.size
      const reverse: boolean = imageData.look || false
      const entry: { time: number; wait: boolean } = imageData.entry || { time: 1, wait: false }
      if (entry.wait) {
        // 表示開始までの遅延処理
        setTimeout(() => {
          this.drawCanvas(img, pos, size, reverse)
        }, entry.time * 1000)
      } else {
        this.drawCanvas(img, pos, size, reverse)
      }
    }
    this.adjustScale(this.gameScreen)
  }

  // previousImage: 同じキーに既存表示があった場合の差し替え前の画像。フェードイン中はこれを下地として
  // 常時全不透明で描画し、新しい画像をその上にクロスフェードさせることで、切り替え中に何も表示されない
  // (黒画面になる)瞬間が生じないようにする。
  async fadeImageIn(
    name: string,
    displayedImages: any,
    duration: number = 1000,
    previousImage?: { image: any; pos: any; size: any; look?: any }
  ): Promise<void> {
    return this.animateImageAlpha(name, displayedImages, 0, 1, duration, previousImage)
  }

  async fadeImageOut(name: string, displayedImages: any, duration: number = 1000): Promise<void> {
    return this.animateImageAlpha(name, displayedImages, 1, 0, duration)
  }

  // displayedImagesをz-index順に描画しつつ、対象(name)の画像だけ透明度をアニメーションさせる。
  // fade()の別キャンバス合成と異なり同一キャンバスに描くため、対象画像が常に最前面になってしまう問題を避けられる。
  private animateImageAlpha(
    name: string,
    displayedImages: any,
    start: number,
    end: number,
    duration: number,
    previousImage?: { image: any; pos: any; size: any; look?: any }
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      const startTime = performance.now()

      const animate = (currentTime: number) => {
        const elapsedTime = currentTime - startTime
        const progress = Math.min(elapsedTime / duration, 1)
        const currentAlpha = start + (end - start) * progress

        this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height)
        for (const [key, imageData] of sortDisplayedImageEntries(displayedImages)) {
          if (!imageData?.image) continue
          if (key === name && previousImage?.image) {
            this.ctx.globalAlpha = 1
            this.drawCanvas(previousImage.image, previousImage.pos || { x: 0, y: 0 }, previousImage.size, previousImage.look)
          }
          const pos = imageData.pos || { x: 0, y: 0 }
          this.ctx.globalAlpha = key === name ? currentAlpha : 1
          this.drawCanvas(imageData.image, pos, imageData.size, imageData.look)
        }
        this.ctx.globalAlpha = 1

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          resolve()
        }
      }

      requestAnimationFrame(animate)
    })
  }

  moveTo(name: string, displayedImages: any, pos: { x: number; y: number }, durning: number) {
    return new Promise((resolve) => {
      const target = displayedImages[name]
      const dest = { x: target.pos.x + Number(pos.x), y: target.pos.y + Number(pos.y) }

      gsap.to(target.pos, {
        x: dest.x,
        y: dest.y,
        duration: durning,
        ease: 'power2.out',
        onUpdate: () => {
          this.show(displayedImages)
        },
        onComplete: () => {
          resolve(null)
        },
      })
    })
  }

  clear(ctx?: CanvasRenderingContext2D) {
    if (ctx === undefined) {
      ctx = this.ctx
    }
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
  }

  drawCanvas(img: ImageObject, pos: any, size: any, reverse: any, ctx?: CanvasRenderingContext2D) {
    if (ctx === undefined) {
      ctx = this.ctx
    }
    const canvas = img.draw(reverse).getCanvas()
    // canvasから画像を取得して、this.ctxに描画（sizeを描画先サイズとして使用し、解像度に合わせて拡縮する）
    // sizeが未指定の場合はcanvas自体のサイズにフォールバックして後方互換を保つ
    const destWidth = size?.width ?? canvas.width
    const destHeight = size?.height ?? canvas.height
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, pos.x, pos.y, destWidth, destHeight)
  }

  adjustScale(targetElement: HTMLElement) {
    // ターゲット要素の元の幅と高さ
    const originalWidth = targetElement.scrollWidth // 例: 1280px
    const originalHeight = targetElement.scrollHeight // 例: 720px

    // ビューポートの幅と高さを取得
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // 幅と高さのスケールを計算
    const scaleX = viewportWidth / originalWidth
    const scaleY = viewportHeight / originalHeight

    // 幅と高さのうち、小さい方のスケールを選択（アスペクト比を維持）
    const scale = Math.min(scaleX, scaleY)

    // スケール後のサイズを計算
    const scaledWidth = originalWidth * scale
    const scaledHeight = originalHeight * scale

    // 中央配置のための位置を計算
    const offsetX = (viewportWidth - scaledWidth) / 2
    const offsetY = (viewportHeight - scaledHeight) / 2

    // ターゲット要素にスケールと位置を適用
    targetElement.style.transformOrigin = 'top left'
    targetElement.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`
  }

  setVisibility(name: string, isVisible: boolean) {
    const target = this.screenHtml.querySelector(name) as HTMLElement
    if (target) {
      target.style.visibility = isVisible ? 'visible' : 'hidden'
    }
  }
}
